import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, apiRaw, enc, productTitle, webImageUrl, type Product } from '../api';
import { useDebounced } from '../hooks';
import { ScanField } from './ScanField';
import { ErrorBox, Loading, Money, Thumb, Val } from './ui';

type Lite = Pick<Product, 'id' | 'itemNumber' | 'listingTitle' | 'manifestTitle'> & Partial<Product>;

/**
 * Find a product by text/item-number search, or by scanning a product barcode
 * that has been linked to it. An unknown barcode is never guessed; the employee
 * may explicitly link it to the product they select.
 */
export function ProductPicker({ onSelect, onUnknownBarcode }: { onSelect: (p: Lite) => void; onUnknownBarcode: (code: string | null) => void }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q.trim(), 300);
  const [scanMsg, setScanMsg] = useState<{ kind: 'warn' | 'error'; text: string; suggestion?: Lite } | null>(null);
  const [scanErr, setScanErr] = useState<unknown>(null);
  const [scanning, setScanning] = useState(false);

  const results = useQuery({
    queryKey: ['products', 'pick', dq],
    queryFn: () =>
      api<{ products: (Product & { webPhotoId: string | null; lotPhotoId: string | null })[] }>(`/products?q=${enc(dq)}`).then((r) => r.products.slice(0, 15)),
    enabled: dq.length >= 2,
  });

  const scan = async (code: string) => {
    setScanning(true);
    setScanMsg(null);
    setScanErr(null);
    try {
      const { body } = await apiRaw(`/scan/product/${enc(code)}`);
      if (body?.product) {
        onUnknownBarcode(null);
        onSelect(body.product);
      } else if (body?.lotLabelMatch) {
        setScanMsg({ kind: 'error', text: `"${code}" is a LOT LABEL (lot ${body.lotLabelMatch}), not a product barcode. Scan the product's own barcode.` });
      } else {
        onUnknownBarcode(code);
        setScanMsg({
          kind: 'warn',
          text: `No product is linked to barcode ${code}. Search for the product below; after selecting it you can link this barcode to it.`,
          suggestion: body?.itemNumberMatch ?? undefined,
        });
      }
    } catch (e) {
      setScanErr(e);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div>
      <ScanField kind="product" onScan={scan} busy={scanning} />
      <ErrorBox error={scanErr} />
      {scanMsg && (
        <div className={`alert alert-${scanMsg.kind}`}>
          {scanMsg.text}
          {scanMsg.suggestion && (
            <div className="mt">
              An item number equals the scanned code. This is <strong>not</strong> confirmed as its barcode.{' '}
              <button className="btn btn-small" onClick={() => onSelect(scanMsg.suggestion!)}>
                Use item {scanMsg.suggestion.itemNumber}
              </button>
            </div>
          )}
        </div>
      )}
      <div className="field">
        <span className="field-label">Search products by item number or words</span>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. 1234567 or kettle" autoComplete="off" />
      </div>
      {results.isFetching && <Loading label="Searching…" />}
      <ErrorBox error={results.error} onRetry={() => results.refetch()} />
      {results.data && results.data.length === 0 && (
        <div className="empty">
          No products match "{dq}". <Link to="/products/new">Create a product manually</Link> (your lot entries are kept).
        </div>
      )}
      {results.data && results.data.length > 0 && (
        <ul className="pick-list">
          {results.data.map((p) => (
            <li key={p.id}>
              <button type="button" className="pick-row" onClick={() => onSelect(p)}>
                {/* A photo so the employee can confirm it is the right item: Costco's, else one from an earlier lot. */}
                {p.webPhotoId ? (
                  <img className="thumb" src={webImageUrl(p.webPhotoId)} width={64} height={64} alt="" loading="lazy" />
                ) : (
                  <Thumb id={p.lotPhotoId} size={64} />
                )}
                <span>
                  <strong className="mono">{p.itemNumber}</strong> <Val v={productTitle(p)} missing="no title" />
                  <span className="muted small">
                    {' '}
                    <Money amount={p.referencePrice ?? p.manifestPrice} currency={p.referenceCurrency ?? p.manifestCurrency} />
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
